<template>
  <div>
    <p
      v-if="error"
      class="text-danger"
      >{{ errorMsg }}</p
    >

    <div
      v-if="initialLoading"
      key="initialLoading"
      class="animate__animated animate__fadeIn animate_fadeOut animate__fast"
    >
      <busy-spinner
        inner-class="my-5"
        text="Loading Items..."
      ></busy-spinner>
    </div>

    <div
      v-if="!initialLoading"
      key="initialLoadingDone"
    >
      <div class="d-flex flex-wrap justify-content-between align-items-end mb-3 animate__animated animate__fast animate__fadeInUp">
        <base-search-box
          :value="currentFilterValues.searchFilter"
          ref="searchBox"
          placeholder="Quick Search..."
          @search="setFilterAndSearch('searchFilter', $event)"
          @reset="setFilterAndSearch('searchFilter', '')"
        ></base-search-box>
        <b-form
          inline
          class="mt-2"
        >
          <div class="align-self-end">
            <b-button
              variant="soft-primary"
              :disabled="!showFilterReset"
              class="text-uppercase"
              @click="clearAllFilters"
            >
              Clear Filters
            </b-button>
          </div>
          <base-filter-select
            label="Product Category"
            v-model="currentFilterValues.categoryFilter"
            :reset-value="initialFilterValues.categoryFilter"
            first-option-text="All Categories"
            :options="setupData.product_cats"
            @change="getNewList"
          ></base-filter-select>
          <b-button
            v-b-tooltip.hover
            title="Refresh List"
            variant="light"
            class="ml-2 align-self-end"
            @click="refreshList(true)"
          >
            <b-icon icon="arrow-repeat"></b-icon>
          </b-button>
        </b-form>
        <!-- <div v-if="!loading && totalRows" class="font-size-90 text-muted">{{totalRows}} Vendors</div> -->
        <!-- <b-button variant="success" @click="handleAdd">
                    <b-icon icon="plus-circle" class="mr-1"></b-icon>
                    Add Vendor
                </b-button> -->
      </div>

      <b-card
        id="item-table-card"
        class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
        no-body
      >
        <div
          v-if="!loading && !totalRows"
          class="d-flex justify-content-center align-items-center"
          style="min-height: 300px"
        >
          <div class="text-center">
            <div class="font-size-100 text-muted mb-4">
              {{ hasAppliedFilters ? 'No items found.  Try a different search' : 'No items yet.  Add one?' }}
            </div>
            <b-button
              v-if="hasAppliedFilters"
              variant="soft-primary"
              @click="clearAllFilters"
            >
              Clear Search
            </b-button>
            <b-button
              v-if="!hasAppliedFilters"
              size="lg"
              variant="soft-success"
              @click="handleAdd"
            >
              Add Item
            </b-button>
          </div>
        </div>

        <b-table
          v-else
          :items="itemList"
          :fields="fields"
          :busy="loading"
          primary-key="id"
          responsive
          no-local-sorting
          no-sort-reset
          :sort-by.sync="sortByField"
          :sort-desc.sync="sortDesc"
          @sort-changed="getNewList"
          hover
          class="no-top-border td-align-middle mb-0"
          thead-class="overflow-hidden cells-align-middle"
          head-variant="light"
        >
          <!-- <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                            <busy-spinner text="Loading..."></busy-spinner>
                        </div>
                    </template> -->
          <template #cell(display_name)="data">
            <b-link
              v-if="!noDetailLinks"
              :href="data.item.url"
              >{{ data.value }}</b-link
            >
            <span v-else>{{ data.value }}</span>
          </template>
          <template #cell(created)="data">
            {{ data.value | dateFilter }}
          </template>
          <template #cell(actions)="data">
            <b-dropdown
              variant="gray-secondary"
              no-caret
              size="sm"
              toggle-class="border shadow-sm"
            >
              <template #button-content>
                <b-icon icon="three-dots"></b-icon>
              </template>
              <b-dropdown-item @click="handleUpdate(data.item.id)">
                <b-icon
                  icon="lightning"
                  variant="warning"
                  class="mr-2"
                ></b-icon>
                Quick View
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!noDetailLinks"
                :href="data.item.url"
              >
                <b-icon
                  icon="card-list"
                  variant="primary"
                  class="mr-2"
                ></b-icon>
                Details
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template #cell(select_column)="data">
            <b-button
              :variant="alreadySelected(data.item) ? 'primary' : 'outline-primary'"
              size="sm"
              @click="handleSelection(data.item)"
              :disabled="!allowDuplicateSelect && alreadySelected(data.item)"
            >
              <b-icon :icon="alreadySelected(data.item) ? 'check' : 'plus'"></b-icon>
              {{ alreadySelected(data.item) ? selectedText : selectText }}
            </b-button>
          </template>
        </b-table>
        <template
          #footer
          v-if="(!loading && totalRows) || alwaysShowFooter"
        >
          <base-table-pagination
            :current-page.sync="currentPage"
            :per-page.sync="perPage"
            :total-items="totalRows"
            show-per-page
            item-name="items"
            @update:perPage="getNewList"
            @update:currentPage="getList"
          ></base-table-pagination>
        </template>
      </b-card>
    </div>
    <!-- end of area to hide when loading -->

    <item-create-update-modal
      ref="itemModal"
      @update-complete="getList"
      @create-complete="getList"
    ></item-create-update-modal>
  </div>
</template>

<script>
  import { suppliosApi } from '../js/api-service';
  import BaseTablePagination from './BaseTablePagination';
  import { tenantInfoMixin } from '@/js/custom-vue-mixins';
  import { FormatDateHelper } from '../js/filters-helpers';
  import BaseSearchBox from './BaseSearchBox';
  import BaseFilterSelect from './BaseFilterSelect';
  import ItemCreateUpdateModal from './ItemCreateUpdateModal'; // using this only for updates, not creates
  import isEqual from 'lodash/isEqual';
  //import cloneDeep from 'lodash/cloneDeep';
  import { splTableDefaults } from '../js/custom-vue-mixins';

  export default {
    name: 'ItemList',
    components: {
      BaseTablePagination,
      BaseSearchBox,
      BaseFilterSelect,
      ItemCreateUpdateModal,
    },
    mixins: [tenantInfoMixin],
    props: {
      includeColumns: {
        // an array of field 'key's to SHOW (of the 'normal' cols)
        // if not included or empty array, we'll show all
        type: Array,
        required: false,
      },
      showSelectColumn: {
        // should we add the 'picker/select/add' column to make this a 'picker' thing?  will fire events with selected row to signal to parent
        type: Boolean,
        default: false,
      },
      selectedIds: {
        // if you're using 'picker/select/add' functionality, this is array of IDs that have already been picked
        type: Array,
        default: () => [],
      },
      selectText: {
        type: String,
        default: 'Add',
      },
      selectedText: {
        type: String,
        default: 'Added',
      },
      allowDuplicateSelect: {
        // if true, you can add the same item multiple times
        type: Boolean,
        default: false,
      },
      noDetailLinks: {
        // if true, we won't have active links on name to go to detail page (use this for picker use cases)
        type: Boolean,
        default: false,
      },
      alwaysShowFooter: {
        // if true, we'll always show the footer, rather than hiding it if no items (helpful to eliminate flash when going page to page)
        type: Boolean,
        default: false,
      },
    },
    data: function () {
      return {
        errorMsg: 'Error',
        error: false,
        initialLoading: true, // set to false after getting list first time
        loading: false, // loading signal for fetching list
        currentPage: 1, // will get sync'd by base-table-pagination
        perPage: 25, // will get sync'd by base-table-pagination -- pull value from table mixin
        totalRows: null, // total items in list... need to set it after we get items from API, and also after filtering is applied with @filtered event
        itemList: [], // list from API
        allFields: [
          {
            key: 'display_name',
            label: 'Item',
            sortable: false,
            api_ordering_name: 'name',
            tdClass: 'font-weight-bold',
          },
          {
            key: 'name',
            label: 'Name',
            sortable: true,
            api_ordering_name: 'name',
            tdClass: '',
          },
          {
            key: 'part_number',
            label: 'Part Number',
            sortable: true,
            api_ordering_name: 'part_number',
          },
          {
            key: 'category_name',
            label: 'Category',
            sortable: true,
            api_ordering_name: 'category__name',
            // PUT new taggings in their own col -- we will keep prouct category/type
            // formatter: (value, key, item) => {
            //   if (this.dynamicTaggingEnabled) {
            //     return item.spl_taggings?.product_category?.map((cat) => cat.name).join(', ') || 'No Category';
            //   }

            //   return value;
            // },
          },
          {
            key: 'created',
            label: 'Date Added',
            sortable: true,
            api_ordering_name: 'created',
            class: 'text-center',
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'text-center',
          },
        ],
        selectColumn: {
          key: 'select_column',
          label: '',
          class: 'text-center',
          sortable: false,
        },
        fields: [], // the FINAL list of fields to show, set this during created hook
        // filtering
        loadingSetupData: false,
        setupData: {}, // various options for filters will go here, like 'product_cats'
        currentFilterValues: {}, // v-models will update this, set it from initialFilterValues in created hook
        initialFilterValues: {
          searchFilter: '',
          categoryFilter: null, // value will be ID of the ProductCategory
        },
        hasAppliedFilters: false, // toggle this on when you are showing filtered results so you know the difference between no results at all, and no results from search
        // for sorting:
        sortByField: 'created', // sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
        sortDesc: true, // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
        defaultSortByField: 'created', // if initial sortByField is not shown based on includeColumns, we default to this -- make sure your ViewSet can sort by this!
      };
    },
    computed: {
      showFilterReset: function () {
        // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
        let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
        return !isSame; // show filter reset if current filter values are NOT SAME as initial filter values
      },
      dynamicTaggingEnabled() {
        return this.$getTenantFlag('taxonomy_product_tags', false);
      },
    },
    filters: {
      dateFilter: function (value) {
        return FormatDateHelper(value);
      },
    },
    created: function () {
      this.resetFilterValues();
      this.perPage = splTableDefaults.perPage;
      let activeFields = [];
      if (this.includeColumns && Array.isArray(this.includeColumns) && this.includeColumns.length) {
        activeFields = this.allFields.filter((fldObj) => this.includeColumns.includes(fldObj.key));
      } else {
        activeFields = this.allFields;
      }
      if (this.showSelectColumn) {
        activeFields.push(this.selectColumn);
      }
      this.fields = activeFields; // do we need vue.set?  only if you want it to be reactive
    },
    mounted: function () {
      this.getSetupData();
      this.getList();
    },
    methods: {
      // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
      refreshList: function (noFilterReset = false) {
        if (noFilterReset) {
          this.getNewList(); // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
        } else {
          this.clearAllFilters(); // this resets all filter values and reloads the list
        }
      },
      handleError: function (error, message) {
        console.log(message);
        console.log(error);
        this.errorMsg = message;
        this.error = true;
      },
      // methods to create/update companies using child CompanyCreateUpdateModal (removed from this component)
      handleUpdate: function (updateItemId) {
        console.log('Calling itemModal ref to startUpdate with ' + updateItemId.toString());
        this.$refs.itemModal.startUpdate(updateItemId);
      },
      handleAdd: function () {
        // only used if list is empty and user clicks on 'add one' button
        this.$refs.itemModal.startCreate();
      },
      getSetupData: function () {
        // get data needed only at beginning, for populating select boxes, etc.
        this.loadingSetupData = true;
        suppliosApi.item
          .getItemSetupOptions()
          .then((response) => (this.setupData = Object.assign({}, response.data)))
          .catch((error) => this.handleError(error, 'Error while getting list options'))
          .finally(() => (this.loadingSetupData = false));
      },
      resetFilterValues: function () {
        this.currentFilterValues = Object.assign({}, this.initialFilterValues);
        // new way
        const filterRefs = ['searchBox'];
        filterRefs.forEach((refName) => {
          if (this.$refs[refName]) {
            this.$refs[refName].reset();
          }
        });
      },
      setFilterAndSearch(filterKey, value) {
        // pass in which filter to set and the value
        console.log('filterKey and value', filterKey, value);
        this.currentFilterValues[filterKey] = value;
        this.getNewList();
      },
      clearAllFilters: function () {
        // clear/reset all filters and search
        this.resetFilterValues();
        this.getNewList();
      },
      getOrderingFieldString: function () {
        // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
        // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
        // also add '-' if sortDesc is true
        // FIRST, HANDLE SPECIAL CASE OF ORDERING BY 'full_name'
        if (this.sortByField === 'full_name') {
          if (this.sortDesc) {
            return '-lname,-fname';
          } else {
            return 'lname,fname';
          }
        }
        let fieldObjForOrdering = this.fields.find((fld) => fld.key === this.sortByField); // returns undefined if not found -- like if you're not showing that column!
        let apiOrderingName = fieldObjForOrdering ? fieldObjForOrdering.api_ordering_name : this.defaultSortByField;
        return (this.sortDesc ? '-' : '') + apiOrderingName;
      },
      getNewList: function () {
        // call getList(), but must reset currentPage first
        // call this from any change to filters, sort, search, etc
        this.currentPage = 1;
        this.$nextTick(() => this.getList());
      },
      getList: function () {
        this.loading = true;
        // axiosConfig['params'] is where we put search/filter params
        let filterParams = {
          // grab the current search/filter values
          search: this.currentFilterValues.searchFilter || null,
          category: this.currentFilterValues.categoryFilter,
          // if you have more filters, put them here
        };
        let orderingParams = {
          ordering: this.getOrderingFieldString(),
        };
        let paginationParams = {
          page: this.currentPage,
          page_size: this.perPage,
        };
        let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
        let axiosConfig = { params: combinedParams };
        suppliosApi.item
          .list(axiosConfig)
          .then((response) => {
            this.totalRows = response.data.count;
            this.itemList = response.data.results;
            this.$emit('list-loaded', this.totalRows); // emit total count to parent
            console.log('Got itemList from getList function');
            console.log(this.itemList);
            // need to set hasAppliedFilters after we get response, not before
            this.hasAppliedFilters = Object.values(filterParams).some((val) => !!val); // goes through all filter values, if any are not empty/null, returns true
          })
          .catch((error) => {
            this.handleError(error, 'Error while loading itemList');
          })
          .finally(() => {
            console.log('hello from finally of getList');
            this.initialLoading = false; // set flag to say we already loaded once
            this.loading = false;
          });
      },
      alreadySelected: function (itemObj) {
        return this.selectedIds.includes(itemObj.id);
      },
      handleSelection: function (itemObj) {
        // emit the event... doing it through this handler function so you can block or modify if needed
        if (!this.allowDuplicateSelect) {
          if (this.alreadySelected(itemObj)) return; // if not allowing duplicate select, check if already added, if so, bail before emit
        }
        this.$emit('item-selected', itemObj);
      },
    },
  };
</script>

<style></style>
